import {
  NON_ACTIONABLE_RA_STATUSES,
  VALID_CARRIER_STATUSES,
  TRANSFER_TYPES
} from 'constants/enum';
import { getCompany } from 'helpers/getCompanyVariables';
import { getStatesWithActiveLicenseNumber } from 'views/AgentInfo/NonResidenceForm/state-license-helpers';
import { isSuncareAppointmentBlocked } from 'helpers/suncare/suncare';
import {
  isFfmState,
  shouldBlockForFfmCertification
} from 'helpers/appointments/ffmCertification';

const company = getCompany();

export const BCBS_CARRIER_NAME = 'BCBS';
export const BCBS_HCSC_CARRIER_NAME = 'BCBS HCSC';
export const BCBS_TEXAS_STATE = 'Texas';
export const BCBS_SOUTH_CAROLINA_STATE = 'South Carolina';

// BCBS only offers South Carolina to agents residing in one of these states
const BCBS_SOUTH_CAROLINA_RESIDENT_STATES = new Set([
  'North Carolina',
  'South Carolina',
  'Georgia'
]);

const NEW_APPOINTMENT_REQUEST_LABEL =
  TRANSFER_TYPES.NewAppointment ?? 'New Appointment';

/** True when the Zoho request is a new contracting request (not a transfer). */
export function isNewAppointmentRequestType(requestType) {
  return requestType === NEW_APPOINTMENT_REQUEST_LABEL;
}

export function isTransferRequestType(appt) {
  return !!appt?.request_type?.toLowerCase().includes('transfer');
}

//Functions for state-carriers

export function getCarrierAPIName(carrierName) {
  if (carrierName === 'NCD Dental & Vision') {
    return 'National_Care';
  }
  if (carrierName === 'Enroll Prime') {
    return 'AFI_Private_Major_Medical_PPO';
  }
  if (carrierName === 'Dean HealthPlan by Medica') {
    return 'Medica';
  }
  return carrierName.split(' ').join('_');
}

export function isAgentAppointedWithCarrier(carrierName, agent) {
  const carrierApiName = carrierName ? getCarrierAPIName(carrierName) : '';

  if (!carrierApiName) return false;

  // carriers that consider states
  if (Array.isArray(agent[carrierApiName]))
    return agent[carrierApiName].length > 0;

  // carriers that do not consider states
  return !!agent[carrierApiName];
}

//Returns the array of states confirmed
export function getConfirmedStates(carrierName, agent) {
  const carrierApiName = carrierName ? getCarrierAPIName(carrierName) : '';

  if (!carrierApiName || !Array.isArray(agent[carrierApiName])) return [];

  const value = agent[carrierApiName];

  if (company === 'sunshine') {
    if (carrierName === 'Medica') return value.filter(s => s !== 'Wisconsin');
    if (carrierName === 'Dean HealthPlan by Medica')
      return value.filter(s => s === 'Wisconsin');
  }

  return value;
}

export function apptHasValidStates(appt) {
  return (
    Array.isArray(appt.appointments_information) &&
    appt.appointments_information.some(isStateInProgress)
  );
}

export function isSubstatusActionable(substatus) {
  return !!substatus && !NON_ACTIONABLE_RA_STATUSES.has(substatus);
}

export function isAppointmentActionable(appt) {
  const validStatuses = new Set(['In Progress', 'In Progress - 2nd Attempt']);
  const isInProgress = validStatuses.has(appt.appointment_status);

  const hasActionableSubstatus = isSubstatusActionable(appt.substatus);

  return hasActionableSubstatus && isInProgress;
}

export function getActionablePendingAppointments(appts) {
  return appts.filter(isAppointmentActionable);
}

export function isStateInProgress(row) {
  return (
    !!row.Appointment_Status &&
    (row.Appointment_Status === 'Not started' ||
      row.Appointment_Status.toLowerCase().includes('progress'))
  );
}

// function to get appointment states from a carrier that considers states
export function getPendingStates(carrierReqAppts, confirmedStates) {
  const allPendingStates = carrierReqAppts.flatMap(appt =>
    appt.carrier === 'BCBS'
      ? appt.uniqueBcbsState
      : Array.isArray(appt.appointments_information) &&
        appt.appointments_information.map(row => row.Appointment_State_1)
  );

  const uniquePendingStates = new Set(
    allPendingStates.filter(
      state =>
        Array.isArray(confirmedStates) && !confirmedStates.includes(state)
    )
  );

  return [...uniquePendingStates].sort((a, b) => a.localeCompare(b));
}

export function getTodaysDateAsInt() {
  //Get the current date in MMDDYYYY format
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  return Number.parseInt(`${mm}${dd}${yyyy}`);
}

export function isDateValid(date) {
  if (!date) return { valid: false, message: 'Expiration date is required' };

  const validFormat = date instanceof Date && !Number.isNaN(date);
  if (!validFormat) return { valid: false, message: 'Invalid date format' };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (date >= today) {
    return { valid: true, message: '' };
  } else {
    return { valid: false, message: 'Expiration date must be in the future' };
  }
}

export function formatDateToMMDDYYYY(dateString) {
  const date = new Date(dateString);

  let month = (date.getMonth() + 1).toString().padStart(2, '0');
  let day = date.getDate().toString().padStart(2, '0');
  let year = date.getFullYear();

  return `${month}${day}${year}`;
}

export function getCarriers(
  carriersByType,
  appts,
  user,
  statuses = ['none'],
  format = 'groupByType'
) {
  if (
    !Array.isArray(statuses) ||
    statuses.some(status => !VALID_CARRIER_STATUSES.has(status))
  ) {
    throw new Error('Invalid statuses param provided');
  }

  const uniqueStatuses = new Set(statuses);

  if (uniqueStatuses.size === 0) {
    uniqueStatuses.add('none');
  }

  const carriersWithRAs = new Set();

  // identify all the unique carrier names in the appts array
  appts.forEach(appt => {
    if (!carriersWithRAs.has(appt.carrier)) {
      carriersWithRAs.add(appt.carrier);
    }
  });

  const carriers = {};

  // get all the different carrier types
  const carrierTypes = Object.keys(carriersByType);

  // initialize the return object with empty objects using the keys (carrier types)
  carrierTypes.forEach(type => (carriers[type] = {}));

  carrierTypes.forEach(type => {
    // get all carrier objects as an array of objects
    const carrierList = Object.values(carriersByType[type]);

    // filter the carriers that where the agent is appointed or has pending RAs
    const validCarriers = carrierList.filter(carrier => {
      // return carriers regardless of status
      if (uniqueStatuses.has('none')) return true;
      // return carriers with pending RAs or Suncare contract awaiting director review
      if (
        uniqueStatuses.has('pending') &&
        (carriersWithRAs.has(carrier.name) ||
          isSuncareAppointmentBlocked(user, carrier.name))
      ) {
        return true;
      }
      // return carriers where agent is appointed
      if (
        uniqueStatuses.has('confirmed') &&
        isAgentAppointedWithCarrier(carrier.name, user)
      ) {
        return true;
      }
      return false;
    });

    // add all carriers to their corresponding group and add a flag
    validCarriers.forEach(carrier => {
      carriers[type][carrier.name] = {
        ...carrier,
        isAppointed: isAgentAppointedWithCarrier(carrier.name, user),
        hasPendingRAs:
          carriersWithRAs.has(carrier.name) ||
          isSuncareAppointmentBlocked(user, carrier.name)
      };
    });
  });

  if (format === 'nameAndFlags') {
    const result = [];

    Object.values(carriers).forEach(type => {
      Object.values(type).forEach(carrier => {
        result.push({
          name: carrier.name,
          isAppointed: carrier.isAppointed,
          hasPendingRAs: carrier.hasPendingRAs
        });
      });
    });

    return result.sort((a, b) => a.name.localeCompare(b.name));
  }

  if (format === 'nameOnly') {
    const result = [];

    Object.values(carriers).forEach(type => {
      Object.values(type).forEach(carrier => {
        result.push(carrier.name);
      });
    });

    return result.sort((a, b) => a.localeCompare(b));
  }

  return carriers;
}

export function getOpenBcbsHcscRequests(requestedAppointmentsByCarrier = {}) {
  if (company !== 'oneill') return [];

  return requestedAppointmentsByCarrier[BCBS_HCSC_CARRIER_NAME] ?? [];
}

export function getOpenBcbsTexasRequests(requestedAppointmentsByCarrier = {}) {
  if (company !== 'oneill') return [];

  return (requestedAppointmentsByCarrier[BCBS_CARRIER_NAME] ?? []).filter(
    appt => appt?.uniqueBcbsState === BCBS_TEXAS_STATE
  );
}

export function hasOpenBcbsHcscRequest(requestedAppointmentsByCarrier = {}) {
  return getOpenBcbsHcscRequests(requestedAppointmentsByCarrier).length > 0;
}

export function hasOpenBcbsTexasRequest(requestedAppointmentsByCarrier = {}) {
  return getOpenBcbsTexasRequests(requestedAppointmentsByCarrier).length > 0;
}

/** O'Neill: BCBS Texas and BCBS HCSC block each other while the other has an open request. */
export function isCarrierBlockedByBcbsCrossRequest(
  carrierName,
  requestedAppointmentsByCarrier = {},
  selectedAppointments = []
) {
  if (company !== 'oneill') return false;

  if (carrierName === BCBS_CARRIER_NAME) {
    return (
      hasOpenBcbsHcscRequest(requestedAppointmentsByCarrier) &&
      selectedAppointments.includes(BCBS_TEXAS_STATE)
    );
  }

  if (carrierName === BCBS_HCSC_CARRIER_NAME) {
    return hasOpenBcbsTexasRequest(requestedAppointmentsByCarrier);
  }

  return false;
}

export function getBcbsCrossRequestBlockMessage(
  carrierName,
  requestedAppointmentsByCarrier = {}
) {
  if (company !== 'oneill') return '';

  if (
    carrierName === BCBS_CARRIER_NAME &&
    hasOpenBcbsHcscRequest(requestedAppointmentsByCarrier)
  ) {
    return 'You currently have a pending BCBS HCSC appointment request. You cannot submit a BCBS Texas appointment request until that request is resolved. To continue, please remove Texas from the selected states.';
  }

  if (
    carrierName === BCBS_HCSC_CARRIER_NAME &&
    hasOpenBcbsTexasRequest(requestedAppointmentsByCarrier)
  ) {
    return 'You currently have a pending BCBS Texas appointment request. You cannot submit a BCBS HCSC appointment request until that request is resolved.';
  }

  return '';
}

// disables the carrier and returns a message when the requested states fall in a blackout period
export function getCarrierDisabledDueToBlackoutData(
  blackoutData,
  appointments,
  appointedType
) {
  const {
    isContractingBlackout,
    contractingBlackoutStates,
    isFullContractingBlackout,
    isTransferBlackout,
    transferBlackoutStates,
    isFullTransferBlackout
  } = blackoutData;

  const isTransferRequest =
    appointedType.includes('Agency') || appointedType.includes('Carrier');

  if (
    isFullContractingBlackout ||
    (isFullTransferBlackout && isTransferRequest)
  )
    return {
      disabled: true,
      message:
        'The carrier is currently in a blackout period. No new appointments can be requested at this time.'
    };

  if (
    isTransferBlackout &&
    isTransferRequest &&
    transferBlackoutStates.some(state => appointments.includes(state))
  )
    return {
      disabled: true,
      message:
        'The carrier is currently in a blackout period for the following states: ' +
        transferBlackoutStates.join(', ') +
        ". No new appointments can be requested at this time, if you'd like to submit a request, please remove the blackout states from the request."
    };

  if (
    isContractingBlackout &&
    contractingBlackoutStates.some(state => appointments.includes(state))
  )
    return {
      disabled: true,
      message:
        'The carrier is currently in a blackout period for the following states: ' +
        contractingBlackoutStates.join(', ') +
        ". No new appointments can be requested at this time, if you'd like to submit a request, please remove the blackout states from the request."
    };

  return { disabled: false, message: '' };
}

export function getRequestedAppointmentsByCarrier(carriers, appts) {
  const carriersWithRAs = {};

  appts.forEach(appt => {
    if (!carriersWithRAs[appt.carrier]) {
      carriersWithRAs[appt.carrier] = [];
    }
    carriersWithRAs[appt.carrier].push(appt);
  });

  carriers.forEach(({ name }) => {
    if (!carriersWithRAs[name]) {
      carriersWithRAs[name] = [];
    }
  });

  return carriersWithRAs;
}

/** Why an offered state can't be requested. Shown on the carrier detail map hover. */
export const STATE_UNAVAILABILITY_REASONS = {
  APPOINTMENTS_DISABLED: 'appointments-disabled',
  NO_LICENSE: 'no-license',
  NO_VALID_FFM: 'no-valid-ffm',
  CARRIER_RESTRICTION: 'carrier-restriction'
};

export const STATE_UNAVAILABILITY_REASON_LABELS = {
  [STATE_UNAVAILABILITY_REASONS.APPOINTMENTS_DISABLED]:
    'This carrier is not accepting appointment requests',
  [STATE_UNAVAILABILITY_REASONS.NO_LICENSE]: 'No active license in this state',
  [STATE_UNAVAILABILITY_REASONS.NO_VALID_FFM]: 'No valid FFM certificate',
  [STATE_UNAVAILABILITY_REASONS.CARRIER_RESTRICTION]:
    'Not available with this carrier for your resident state'
};

// carrier-specific state exclusions that don't depend on licenses
function isStateRestrictedByCarrierRules(carrier, user, state) {
  const name = carrier.name;
  const residentState = user.Resident_Insurance_License_State;

  if (company === 'sunshine') {
    if (name === 'Molina' && residentState !== 'Florida') {
      return state === 'Florida';
    }
    return name === 'Medica' && state === 'Wisconsin';
  }

  if (name === 'United Health Care' && residentState === 'Nevada') {
    return state === 'Nevada';
  }

  if (
    name === BCBS_CARRIER_NAME &&
    !BCBS_SOUTH_CAROLINA_RESIDENT_STATES.has(residentState)
  ) {
    return state === BCBS_SOUTH_CAROLINA_STATE;
  }

  return false;
}

/**
 * Lists every reason a state offered by the carrier can't be requested by this agent.
 * An empty array means the state is requestable.
 * @param carrier - carrier data from the carrier factory
 * @param user - agent Zoho data
 * @param state - full state name, e.g. `'California'`
 * @param licensedStates - optional precomputed Set of licensed state names, to avoid rebuilding it per state
 */
export function getCarrierStateUnavailabilityReasons(
  carrier,
  user,
  state,
  licensedStates = null
) {
  const reasons = [];
  if (!carrier || !state) return reasons;

  if (carrier.appointmentsDisabled) {
    reasons.push(STATE_UNAVAILABILITY_REASONS.APPOINTMENTS_DISABLED);
  }

  const licensed =
    licensedStates ?? new Set(getStatesWithActiveLicenseNumber(user));
  if (!licensed.has(state)) {
    reasons.push(STATE_UNAVAILABILITY_REASONS.NO_LICENSE);
  }

  const isFfmBlocked = shouldBlockForFfmCertification(
    carrier.type,
    user?.FFM_Certification_Expiration
  );
  if (isFfmBlocked && isFfmState(state)) {
    reasons.push(STATE_UNAVAILABILITY_REASONS.NO_VALID_FFM);
  }

  if (isStateRestrictedByCarrierRules(carrier, user, state)) {
    reasons.push(STATE_UNAVAILABILITY_REASONS.CARRIER_RESTRICTION);
  }

  return reasons;
}

/**
 * Filters the states a carrier offers down to the ones this agent can actually request.
 * @param carrier - carrier data from the carrier factory
 * @param user - agent Zoho data
 * @param tempOptions - offered states minus the ones already confirmed/pending
 */
export function getCarrierStateOptions(carrier, user, tempOptions) {
  if (!carrier) return [];

  const licensedStates = new Set(getStatesWithActiveLicenseNumber(user));

  return tempOptions.filter(
    state =>
      getCarrierStateUnavailabilityReasons(carrier, user, state, licensedStates)
        .length === 0
  );
}

export function getActionableApptCarriers(
  carriersByType,
  actionableAppts,
  format = 'groupByType'
) {
  const uniqueActionableCarriers = new Set();

  // identify all the unique carrier names in the appts array
  actionableAppts.forEach(appt => {
    if (!uniqueActionableCarriers.has(appt.carrier)) {
      uniqueActionableCarriers.add(appt.carrier);
    }
  });

  const carriers = {};

  // get all the different carrier types
  const carrierTypes = Object.keys(carriersByType);

  // initialize the return object with empty objects using the keys (carrier types)
  carrierTypes.forEach(type => (carriers[type] = {}));

  carrierTypes.forEach(type => {
    // get all carrier objects as an array of objects
    const carrierList = Object.values(carriersByType[type]);

    // filter the carriers that have pending actions
    const actionableCarriers = carrierList.filter(carrier =>
      uniqueActionableCarriers.has(carrier.name)
    );

    // add all actionable carriers to their corresponding group
    actionableCarriers.forEach(carrier => {
      carriers[type][carrier.name] = carrier;
    });
  });

  if (format === 'nameOnly') {
    const result = [];

    Object.values(carriers).forEach(type => {
      Object.values(type).forEach(carrier => {
        result.push(carrier.name);
      });
    });

    return result;
  }

  // the return object will have at least one carrier in any carrier group
  return carriers;
}

export function getCarrierDisplayName(carrierName) {
  if (company === 'sunshine' && carrierName === 'Dean HealthPlan by Medica') {
    return 'Medica (Dean HealthPlan)';
  }
  return carrierName;
}

export function getParentCarrierName(carrierName) {
  if (company === 'sunshine' && carrierName === 'Dean HealthPlan by Medica') {
    return 'Medica';
  }
  return carrierName;
}

export const STALL_DAYS_BLUE_LT = 14;
export const STALL_DAYS_RED_GTE = 21;

export function getStalledDaysSinceInProgress(createdTime) {
  if (createdTime == null || createdTime === '') return null;
  const created = new Date(createdTime);
  if (Number.isNaN(created.getTime())) return null;
  const start = new Date(
    created.getFullYear(),
    created.getMonth(),
    created.getDate()
  );
  const today = new Date();
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.floor((end - start) / (1000 * 60 * 60 * 24));
}

/** Top accent bar on appointment cards (see AppointmentCard). */
export function getStallAccentBarClass(stalledDays) {
  if (stalledDays == null || stalledDays < STALL_DAYS_BLUE_LT) {
    return 'bg-primary-emphasis-light';
  }
  if (stalledDays >= STALL_DAYS_RED_GTE) return 'bg-semantic-danger';
  return 'bg-pending';
}

/** Worst-case stalled days for an agent’s queue; null if no row has a computed value. */
export function getMaxStalledDaysAmongAppointments(appointments) {
  if (!appointments?.length) return null;
  let max = null;
  for (const appt of appointments) {
    const d = appt.Stalled_Days;
    if (d == null) continue;
    max = max === null ? d : Math.max(max, d);
  }
  return max;
}

export function getStallSeverityPillClassNames(stalledDays) {
  if (stalledDays == null || stalledDays < STALL_DAYS_BLUE_LT) {
    return 'border-primary-emphasis-light/50 bg-primary-emphasis-light/15 text-primary-darkened';
  }
  if (stalledDays >= STALL_DAYS_RED_GTE) {
    return 'border-semantic-danger/50 bg-semantic-danger/10 text-semantic-danger';
  }
  return 'border-pending/50 bg-pending/15 text-warning-contrast';
}

export function getStallAvatarClassNames(stalledDays) {
  if (stalledDays == null || stalledDays < STALL_DAYS_BLUE_LT) {
    return 'bg-primary-emphasis-light/15 text-primary-emphasis-light border border-primary-emphasis-light';
  }
  if (stalledDays >= STALL_DAYS_RED_GTE) {
    return 'bg-semantic-danger/15 text-semantic-danger border border-semantic-danger';
  }
  return 'bg-pending/15 text-pending border border-pending';
}

// true when the appt request is a recertification (no state subform, O'Neill only)
export function isRecertificationRequest(appt) {
  return Boolean(
    TRANSFER_TYPES?.Recertification &&
      appt?.request_type === TRANSFER_TYPES.Recertification
  );
}
